import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Envelope, Phone, Storefront, User, ChatText, CheckCircle } from "@phosphor-icons/react";

export default function Register() {
  const { register, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", name: "", businessName: "" });
  const [leadForm, setLeadForm] = useState({ name: "", email: "", business_name: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [leadLoading, setLeadLoading] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/status`)
      .then(r => r.json())
      .then(s => {
        if (s.registration_enabled === false || s.registration_enabled === "false") {
          setShowLead(true);
        }
      })
      .catch(() => {});
  }, []);

  const update = (k, v) => setForm({ ...form, [k]: v });
  const updateLead = (k, v) => setLeadForm({ ...leadForm, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    const res = await register({
      email: form.email,
      password: form.password,
      name: form.name,
      businessName: form.businessName,
    });
    setLoading(false);
    if (res.ok) {
      if (res.requiresConfirmation) {
        toast.success("Check your email to confirm your account!");
      } else {
        toast.success("Account created!");
        navigate("/dashboard", { replace: true });
      }
    } else {
      if (res.error?.toLowerCase().includes("invitation")) setShowLead(true);
      toast.error(res.error);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(leadForm),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "We'll be in touch soon!");
        setLeadSubmitted(true);
      } else {
        toast.error(data.detail || "Something went wrong");
      }
    } catch {
      toast.error("Service unavailable");
    } finally {
      setLeadLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const res = await signInWithGoogle();
    if (!res.ok) toast.error(res.error);
  };

  if (showLead || leadSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 py-10" style={{ background: "var(--bg)" }}>
        <div className="w-full max-w-lg">
          <Link to="/" className="flex items-center gap-2 justify-center mb-10">
            <div className="h-10 w-10 rounded-xl flex items-center justify-center font-black text-white"
                 style={{ background: "var(--primary)", border: "1.5px solid var(--ink)", boxShadow: "2px 2px 0 0 var(--ink)" }}>
              RA
            </div>
            <span className="display font-black text-2xl">Rating Ally</span>
          </Link>

          <div className="card-pop p-8 sm:p-10">
            {leadSubmitted ? (
              <div className="text-center py-6">
                <div className="mx-auto h-14 w-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircle size={28} className="text-green-600" weight="fill" />
                </div>
                <h1 className="display text-2xl font-black tracking-tighter mb-2">You're on the list!</h1>
                <p className="text-sm text-[var(--ink-muted)]">Our team will reach out soon to set up your account. Keep an eye on your inbox.</p>
                <Link to="/" className="btn-primary w-full justify-center mt-8 inline-flex">Go home</Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h1 className="display text-3xl font-black tracking-tighter">Join the waitlist</h1>
                  <p className="text-sm text-[var(--ink-muted)] mt-2 leading-relaxed">
                    We're onboarding new partners by invitation. Leave your details and we'll reach out soon.
                  </p>
                </div>

                <form onSubmit={submitLead} className="space-y-5">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                      Your name
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-0 bottom-0 flex items-center pointer-events-none">
                        <User size={18} weight="regular" className="text-gray-400" />
                      </div>
                      <input
                        required
                        className="input-pop w-full h-11 pr-4 text-sm"
                        style={{ paddingLeft: "44px" }}
                        value={leadForm.name}
                        onChange={e => updateLead("name", e.target.value)}
                        placeholder="Jane Doe"
                      />
                    </div>
                  </div>

                  {/* Business */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                      Business name
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-0 bottom-0 flex items-center pointer-events-none">
                        <Storefront size={18} weight="regular" className="text-gray-400" />
                      </div>
                      <input
                        required
                        className="input-pop w-full h-11 pr-4 text-sm"
                        style={{ paddingLeft: "44px" }}
                        value={leadForm.business_name}
                        onChange={e => updateLead("business_name", e.target.value)}
                        placeholder="Sunset Cafe"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-0 bottom-0 flex items-center pointer-events-none">
                        <Envelope size={18} weight="regular" className="text-gray-400" />
                      </div>
                      <input
                        type="email"
                        required
                        className="input-pop w-full h-11 pr-4 text-sm"
                        style={{ paddingLeft: "44px" }}
                        value={leadForm.email}
                        onChange={e => updateLead("email", e.target.value)}
                        placeholder="you@cafe.com"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                      Phone <span className="normal-case font-medium opacity-50 ml-1">(optional)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-0 bottom-0 flex items-center pointer-events-none">
                        <Phone size={18} weight="regular" className="text-gray-400" />
                      </div>
                      <input
                        className="input-pop w-full h-11 pr-4 text-sm"
                        style={{ paddingLeft: "44px" }}
                        value={leadForm.phone}
                        onChange={e => updateLead("phone", e.target.value)}
                        placeholder="+91 98765 43210"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)] mb-2">
                      Message <span className="normal-case font-medium opacity-50 ml-1">(optional)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3.5 top-3 pointer-events-none">
                        <ChatText size={18} weight="regular" className="text-gray-400" />
                      </div>
                      <textarea
                        className="input-pop w-full min-h-[96px] pr-4 text-sm resize-none"
                        style={{ paddingLeft: "44px", paddingTop: "12px", paddingBottom: "12px" }}
                        value={leadForm.message}
                        onChange={e => updateLead("message", e.target.value)}
                        placeholder="Tell us about your business..."
                      />
                    </div>
                  </div>

                  <button
                    disabled={leadLoading}
                    type="submit"
                    className="btn-primary w-full justify-center mt-2 h-11 hover:opacity-90 transition-opacity disabled:opacity-60"
                  >
                    {leadLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="inline-block h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting…
                      </span>
                    ) : (
                      <>Get in touch <ArrowRight size={16} weight="bold" /></>
                    )}
                  </button>
                </form>

                <div className="mt-6 text-sm text-center text-[var(--ink-muted)]">
                  Already have access?{" "}
                  <Link to="/login" className="font-bold text-[var(--ink)] underline hover:no-underline">Sign in</Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10" style={{ background: "var(--bg)" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center font-black text-white"
               style={{ background: "var(--primary)", border: "1.5px solid var(--ink)", boxShadow: "2px 2px 0 0 var(--ink)" }}>
            RA
          </div>
          <span className="display font-black text-2xl">Rating Ally</span>
        </Link>

        <div className="card-pop p-7 sm:p-9">
          <h1 className="display text-3xl font-black tracking-tighter">Create your account</h1>
          <p className="text-sm text-[var(--ink-muted)] mt-1">Start collecting reviews in minutes.</p>

          <button
            onClick={handleGoogleSignIn}
            className="btn-pop w-full justify-center mt-6 flex items-center gap-2"
            type="button"
          >
            <GoogleLogo />
            Continue with Google
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: "var(--border)" }}></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-[var(--ink-muted)]">or</span>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Your name</label>
                <input required className="input-pop mt-1.5" value={form.name} onChange={(e)=>update("name", e.target.value)} placeholder="Jane Doe" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Business</label>
                <input required className="input-pop mt-1.5" value={form.businessName} onChange={(e)=>update("businessName", e.target.value)} placeholder="Sunset Cafe" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Email</label>
              <input type="email" required className="input-pop mt-1.5" value={form.email} onChange={(e)=>update("email", e.target.value)} placeholder="you@cafe.com" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Password</label>
              <input type="password" required minLength={6} className="input-pop mt-1.5" value={form.password} onChange={(e)=>update("password", e.target.value)} placeholder="At least 6 characters" />
            </div>
            <button disabled={loading} type="submit" className="btn-primary w-full justify-center mt-2">
              {loading ? "Creating…" : (<>Create account <ArrowRight size={16} weight="bold" /></>)}
            </button>
          </form>

          <div className="mt-6 text-sm text-center text-[var(--ink-muted)]">
            Already have an account?{" "}
            <Link to="/login" className="font-bold text-[var(--ink)] underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}